import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { calcDeduzioneNottePassiva } from '../components/Turni/nottePassiva';

const ABSENCE_TYPES = ['ferie', 'permesso', 'malattia'];

const MESI_IT = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

const fmt = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

const italianWeekday = (fullDate) => {
  const d = new Date(`${fullDate}T12:00:00`);
  const name = d.toLocaleDateString('it-IT', { weekday: 'long' });
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const calcDuration = (start_time, end_time) => {
  if (!start_time || !end_time) return 0;
  const [sH, sM] = start_time.split(':').map(Number);
  const [eH, eM] = end_time.split(':').map(Number);
  let d = (eH + eM / 60) - (sH + sM / 60);
  if (d < 0) d += 24;
  return d;
};

const fmtDuration = (hours) => {
  if (!hours || hours <= 0) return '';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const absenceLabel = (type) => {
  if (type === 'malattia') return 'Malattia';
  if (type === 'permesso') return 'Permesso';
  return 'Ferie';
};

const monthLabel = (fullDate) => {
  if (!fullDate) return '';
  const [y, m] = fullDate.split('-');
  return `${MESI_IT[parseInt(m, 10) - 1]} ${y}`;
};

// Calcola le statistiche mensili per un collaboratore (esportata per uso esterno)
export const calcMonthlyStats = (allDays, turniData, managerFerieList, user) => {
  const uName = `${user.nome} ${user.cognome}`.trim();
  const allDates = allDays.map(d => d.fullDate);

  const monthShifts = turniData.filter(t => t.operator_name === uName && allDates.includes(t.date));
  const dailyOre = parseFloat(user.ore_giornaliere_previste || 0);

  let expectedHours = 0, workedHours = 0, ferieHours = 0, permessiHours = 0, malattiaHours = 0, overtimeHours = 0;

  monthShifts.forEach(t => {
    const dur = calcDuration(t.start_time, t.end_time);
    if (t.shift_type === 'ferie') {
      ferieHours += dur > 1 ? dur : dailyOre;
    } else if (t.shift_type === 'permesso') {
      permessiHours += dur > 0 ? dur : dailyOre;
    } else if (t.shift_type === 'malattia') {
      malattiaHours += dur > 1 ? dur : dailyOre;
    } else {
      const counted = dur - calcDeduzioneNottePassiva(t);
      expectedHours += counted;
      if (t.status === 'timbrato') workedHours += counted;
      if (parseInt(t.is_overtime, 10) === 1) overtimeHours += counted;
    }
  });

  // Conta le leave request approvate non riflesse come turno diretto
  managerFerieList
    .filter(lv => lv.operator_name === uName && lv.status === 'approvata')
    .forEach(lv => {
      allDates.forEach(d => {
        if (lv.start_date <= d && lv.end_date >= d) {
          const hasShift = monthShifts.some(t => t.date === d && ABSENCE_TYPES.includes(t.shift_type));
          if (!hasShift) {
            const dur = calcDuration(lv.start_time, lv.end_time);
            const counted = dur > 1 ? dur : dailyOre;
            const type = (lv.leave_type || 'ferie').toLowerCase();
            if (type === 'ferie') ferieHours += counted;
            else if (type === 'permesso') permessiHours += counted;
            else if (type === 'malattia') malattiaHours += counted;
          }
        }
      });
    });

  return {
    oreMensiliContratto: parseFloat(user.ore_mensili_previste || 0),
    expectedHours: +Number(expectedHours).toFixed(1),
    workedHours: +Number(workedHours).toFixed(1),
    ferieHours: +Number(ferieHours).toFixed(1),
    permessiHours: +Number(permessiHours).toFixed(1),
    malattiaHours: +Number(malattiaHours).toFixed(1),
    overtimeHours: +Number(overtimeHours).toFixed(1),
  };
};

// Costruisce la riga per ogni giorno del mese
const buildDayRows = (allDays, turniData, managerFerieList, uName) =>
  allDays.map(d => {
    const date = fmt(d.fullDate);
    const giorno = italianWeekday(d.fullDate);

    // Prima: leave request approvata (ferie/permesso/malattia tramite richiesta)
    const leave = managerFerieList.find(
      lv => lv.operator_name === uName
        && lv.status === 'approvata'
        && lv.start_date <= d.fullDate
        && lv.end_date >= d.fullDate
    );

    if (leave) {
      const type = (leave.leave_type || 'ferie').toLowerCase();
      let timeRange = '';
      if (leave.start_time && leave.start_time.substring(0, 5) !== '00:00' && leave.end_time) {
        timeRange = ` ${leave.start_time.substring(0, 5)}-${leave.end_time.substring(0, 5)}`;
      }
      return [date, giorno, `${absenceLabel(type)}${timeRange}`, '', leave.motivazione || '', ''];
    }

    // Poi: turni del giorno
    const shifts = turniData.filter(t => t.operator_name === uName && t.date === d.fullDate);

    if (shifts.length === 0) {
      return [date, giorno, 'Riposo', '', '', ''];
    }

    const lines = shifts.map(s => {
      if (ABSENCE_TYPES.includes(s.shift_type)) {
        let timeRange = '';
        if (s.start_time && s.start_time.substring(0, 5) !== '00:00' && s.end_time) {
          timeRange = ` ${s.start_time.substring(0, 5)}-${s.end_time.substring(0, 5)}`;
        }
        return absenceLabel(s.shift_type) + timeRange;
      }
      const start = s.start_time ? s.start_time.substring(0, 5) : '?';
      const end = s.end_time ? s.end_time.substring(0, 5) : '?';
      const period = s.shift_period === 'giorno' ? ' [G]'
        : s.shift_period === 'notte' ? ' [N]'
        : s.shift_period === 'coda_della_notte' ? ' [C]'
        : '';
      const ot = parseInt(s.is_overtime, 10) === 1 ? ' (Str.)' : '';
      return `${start}-${end}${period}${ot}`;
    });

    const allAbsences = shifts.every(s => ABSENCE_TYPES.includes(s.shift_type));
    const stato = allAbsences ? ''
      : shifts.some(s => s.status === 'timbrato') ? 'Timbrato' : 'Da timbrare';
    const firstNote = shifts[0]?.notes && String(shifts[0].notes).trim() !== '' && String(shifts[0].notes) !== '0'
      ? String(shifts[0].notes) : '';

    const totalWorked = shifts
      .filter(s => !ABSENCE_TYPES.includes(s.shift_type))
      .reduce((acc, s) => acc + calcDuration(s.start_time, s.end_time), 0);
    const oreLabel = fmtDuration(totalWorked);

    return [date, giorno, lines.join('\n'), oreLabel, firstNote, stato];
  });

// allDays: array di {date, fullDate, ...} già filtrati (senza i null di padding)
// cumulativeBalances: { 'Nome Cognome': deltaOre } — somma (effettive - contratto) da gen. ad oggi
export const generateMensialePdf = ({ allDays, collaboratorsList, turniData, managerFerieList, cumulativeBalances = {} }) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const periodo = allDays[0]?.fullDate ? monthLabel(allDays[0].fullDate) : '';
  const today = new Date().toLocaleDateString('it-IT');
  const pageW = doc.internal.pageSize.width;
  const pageH = doc.internal.pageSize.height;

  collaboratorsList.forEach((user, idx) => {
    if (idx > 0) doc.addPage();

    const uName = `${user.nome} ${user.cognome}`.trim();

    // Header bar colorata
    doc.setFillColor(41, 128, 185);
    doc.rect(0, 0, pageW, 14, 'F');
    doc.setTextColor(255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Casa Letizia — Resoconto Mensile Turni', 14, 9);

    doc.setTextColor(0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text(`Collaboratore: ${uName}`, 14, 24);
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text(`Mese: ${periodo}`, 14, 31);
    doc.text(`Generato il: ${today}`, 14, 37);
    doc.setTextColor(0);

    // Tabella riepilogo ore mensili
    const stats = calcMonthlyStats(allDays, turniData, managerFerieList, user);

    const oreEffettive = +Number(
      stats.workedHours + stats.ferieHours + stats.permessiHours + stats.malattiaHours
    ).toFixed(1);

    const saldo = cumulativeBalances[uName] !== undefined ? cumulativeBalances[uName] : null;
    const saldoCell = saldo === null
      ? { content: '—', styles: { halign: 'center' } }
      : {
          content: `${saldo >= 0 ? '+' : ''}${saldo}h`,
          styles: {
            halign: 'center',
            fontStyle: 'bold',
            textColor: saldo >= 0 ? [0, 128, 0] : [180, 0, 0],
          },
        };

    autoTable(doc, {
      startY: 44,
      head: [['Contratto', 'Assegnate', 'Svolte', 'Straord.', 'Ferie', 'Permessi', 'Malattia', 'Effettive', 'Debito/Accredito']],
      body: [[
        `${stats.oreMensiliContratto}h`,
        `${stats.expectedHours}h`,
        `${stats.workedHours}h`,
        `${stats.overtimeHours}h`,
        `${stats.ferieHours}h`,
        `${stats.permessiHours}h`,
        `${stats.malattiaHours}h`,
        `${oreEffettive}h`,
        saldoCell,
      ]],
      styles: { fontSize: 8, cellPadding: 2.5, halign: 'center' },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 7.5 },
      theme: 'grid',
    });

    // Tabella giorno per giorno
    const dayRows = buildDayRows(allDays, turniData, managerFerieList, uName);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [['Data', 'Giorno', 'Orario / Assenza', 'Ore lav.', 'Note', 'Stato']],
      body: dayRows,
      styles: { fontSize: 8.5, cellPadding: 2.5, overflow: 'linebreak' },
      headStyles: { fillColor: [52, 73, 94], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 25 },
        2: { cellWidth: 48 },
        3: { cellWidth: 20, halign: 'center' },
        4: { cellWidth: 46 },
        5: { cellWidth: 24 },
      },
      theme: 'striped',
      alternateRowStyles: { fillColor: [245, 248, 252] },
    });

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(160);
    doc.text(`Casa Letizia — Documento riservato — ${today}`, 14, pageH - 8);
    doc.setTextColor(0);
  });

  return doc;
};
